import React, { useState } from "react";
import { Trash2, Loader2, Plus, Check, Sparkles, ShieldCheck, Trophy } from "lucide-react";
import toast from "react-hot-toast";
import { Button, IconButton, Input } from "../../atoms";
import {
  useLeadOnboarding, useCreateOnboardingItem, useUpdateOnboardingItem, useDeleteOnboardingItem,
  useMarkLeadOnboarded, useUnmarkLeadOnboarded,
} from "../../hooks/useLeads";
import ConfirmDeleteModal from "../../overlays/ConfirmDeleteModal";

// Suggested defaults for new won leads — quick-add chips. Only shown when the
// checklist is empty so we don't badger people who already curated their list.
const SEEDS = [
  "Kickoff call scheduled",
  "Welcome email sent",
  "Contract signed",
  "Payment received",
  "Access provisioned",
  "Training session booked",
];

function OnboardingRow({ leadId, item }) {
  const update = useUpdateOnboardingItem(leadId);
  const del = useDeleteOnboardingItem(leadId);
  const done = !!item.completedAt;
  const [confirmOpen, setConfirmOpen] = useState(false);

  const toggle = async () => {
    try { await update.mutateAsync({ id: item._id, patch: { completed: !done } }); }
    catch (err) { toast.error(err?.response?.data?.error || err.message || "Update failed"); }
  };

  const remove = () => setConfirmOpen(true);

  const performDelete = async () => {
    try {
      await del.mutateAsync(item._id);
      setConfirmOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Delete failed");
    }
  };

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "8px 12px", borderRadius: 8,
      background: done ? "var(--color-bg-muted)" : "var(--color-bg-surface)",
      border: "1px solid var(--color-border-subtle)",
    }}>
      <button
        onClick={toggle}
        disabled={update.isPending}
        aria-label={done ? "Reopen item" : "Mark complete"}
        style={{
          width: 20, height: 20, flexShrink: 0,
          borderRadius: 6,
          border: `1.5px solid ${done ? "var(--crm-primary)" : "var(--color-border-strong, var(--color-border-subtle))"}`,
          background: done ? "var(--crm-primary)" : "transparent",
          color: "white", cursor: "pointer",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}
      >
        {done && <Check size={13} />}
      </button>
      <div style={{
        flex: 1, fontSize: 14, color: "var(--color-text-primary)",
        textDecoration: done ? "line-through" : "none",
        opacity: done ? 0.7 : 1,
      }}>{item.title}</div>
      <IconButton aria-label="Delete item" onClick={remove} disabled={del.isPending}>
        {del.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
      </IconButton>
      <ConfirmDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={performDelete}
        title="Delete onboarding item?"
        message={`"${item.title}" will be removed.`}
        confirmLabel="Delete item"
        isPending={del.isPending}
      />
    </div>
  );
}

export default function OnboardingPanel({ leadId, lead }) {
  const { data, isLoading } = useLeadOnboarding(leadId);
  const create = useCreateOnboardingItem(leadId);
  const markOnboarded = useMarkLeadOnboarded(leadId);
  const unmarkOnboarded = useUnmarkLeadOnboarded(leadId);
  const [title, setTitle] = useState("");
  const items = data?.items || [];
  const done = items.filter((i) => i.completedAt).length;
  const pct = items.length === 0 ? 0 : Math.round((done / items.length) * 100);
  const isOnboarded = !!lead?.onboardedAt;
  const canMark = !isOnboarded && items.length > 0 && pct === 100;

  const handleMark = async () => {
    try {
      await markOnboarded.mutateAsync();
      toast.success("Marked as onboarded");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed");
    }
  };

  const handleUnmark = async () => {
    try {
      await unmarkOnboarded.mutateAsync();
      toast.success("Moved back to onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed");
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    const t = title.trim();
    if (!t) return;
    try { await create.mutateAsync({ title: t }); setTitle(""); }
    catch (err) { toast.error(err?.response?.data?.error || err.message || "Create failed"); }
  };

  const addSeed = async (t) => {
    try { await create.mutateAsync({ title: t }); }
    catch (err) { toast.error(err?.response?.data?.error || err.message || "Create failed"); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {lead?.status !== "won" && (
        <div style={{
          background: "var(--color-bg-muted)", borderRadius: 8, padding: 10,
          fontSize: 13, color: "var(--color-text-secondary)",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <Sparkles size={14} style={{ color: "var(--crm-primary)" }} />
          Onboarding usually starts after a lead is marked won — you can build the checklist now anyway.
        </div>
      )}

      {items.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1, height: 6, background: "var(--color-bg-muted)", borderRadius: 999, overflow: "hidden" }}>
            <div style={{ width: `${pct}%`, height: "100%", background: "var(--crm-primary)", transition: "width 200ms ease" }} />
          </div>
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", fontVariantNumeric: "tabular-nums" }}>
            {done} / {items.length} · {pct}%
          </div>
        </div>
      )}

      <form onSubmit={submit} style={{ display: "flex", gap: 8 }}>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Add an onboarding step"
          style={{ flex: 1 }}
        />
        <Button type="submit" variant="primary" size="md" disabled={create.isPending || !title.trim()} leftIcon={create.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}>
          Add
        </Button>
      </form>

      {!isLoading && items.length === 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ color: "var(--color-text-secondary)", fontSize: 13, padding: "10px 0" }}>
            No items yet. Start with one of these:
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {SEEDS.map((s) => (
              <button
                key={s}
                onClick={() => addSeed(s)}
                disabled={create.isPending}
                style={{
                  fontSize: 12, padding: "5px 10px", borderRadius: 999,
                  background: "var(--color-bg-muted)",
                  border: "1px solid var(--color-border-subtle)",
                  color: "var(--color-text-primary)", cursor: "pointer",
                }}
              >+ {s}</button>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {isLoading && (
          <div style={{ color: "var(--color-text-secondary)", fontSize: 13, padding: 20, textAlign: "center" }}>
            <Loader2 size={16} className="animate-spin" />
          </div>
        )}
        {items.map((item) => <OnboardingRow key={item._id} leadId={leadId} item={item} />)}
      </div>

      {/* Onboarded state + Mark/Undo CTA. Three states:
            1) Onboarded → show "Onboarded on <date>" + small "Move back" link.
            2) 100% complete but not onboarded → prominent "Mark as Onboarded" CTA.
            3) Anything else → no CTA (progress bar above is enough). */}
      {isOnboarded ? (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          padding: "10px 14px",
          background: "var(--crm-success-soft, color-mix(in srgb, var(--crm-success) 10%, transparent))",
          color: "var(--crm-success)",
          border: "1px solid color-mix(in srgb, var(--crm-success) 35%, transparent)",
          borderRadius: 8, fontSize: 13, fontWeight: 600,
        }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <ShieldCheck size={15} /> Onboarded on {new Date(lead.onboardedAt).toLocaleDateString()}
          </span>
          <button
            onClick={handleUnmark}
            disabled={unmarkOnboarded.isPending}
            style={{
              background: "transparent", border: "none", color: "inherit",
              fontSize: 12, fontWeight: 600, cursor: "pointer", opacity: 0.85,
              display: "inline-flex", alignItems: "center", gap: 4,
            }}
          >
            {unmarkOnboarded.isPending ? <Loader2 size={12} className="animate-spin" /> : <Trophy size={12} />}
            Move back
          </button>
        </div>
      ) : canMark ? (
        <div style={{
          padding: 14, borderRadius: 8,
          background: "color-mix(in srgb, var(--crm-success) 10%, transparent)",
          border: "1px solid color-mix(in srgb, var(--crm-success) 35%, transparent)",
          display: "flex", flexDirection: "column", gap: 10,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--crm-success)", fontWeight: 600, fontSize: 13 }}>
            <ShieldCheck size={15} /> All tasks complete
          </div>
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
            Mark this lead as onboarded to move it from the onboarding list to the onboarded list.
          </div>
          <Button
            variant="primary"
            size="md"
            onClick={handleMark}
            disabled={markOnboarded.isPending}
            leftIcon={markOnboarded.isPending ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            style={{ alignSelf: "flex-start", background: "var(--crm-success)", borderColor: "var(--crm-success)" }}
          >
            Mark as Onboarded
          </Button>
        </div>
      ) : null}
    </div>
  );
}
